import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DownloadOutlined } from "@ant-design/icons";

import Button from "../../components/button";
import { InputLabelSelect } from "../../components/inputLabel";
import PageTitle from "../../components/pageTitle";
import impostoRendaActions from "../../redux/impostoRenda/actions";
import { StoreState } from "../../redux/reduxTypes";
import { ImpostoRendaState } from "../../types/state.types";
import {
  AreaObservacao,
  AreaInfo,
  ContainerPagina,
  FormFiltros,
  AreaFontePagadora,
  AreaBeneficiario,
  LinhasTabela,
  AreaInfoComplementares,
  AreaResponsavelInfo,
} from "./styled";
import ministerioEconomia from "../../images/emblema-ministerio-economia.png";
import { getSession } from "../../utils/session-helper";
import {
  downloadPDFPaginaUnica,
  formatCPF,
  formatData,
  formatMoney,
} from "../../utils/functions-helper";
import { message } from "antd";
import IndicadorSemDados from "../../components/indicadorSemDados";

function ImpostoRenda() {
  const dispatch = useDispatch();
  const { filtros, listaAnos, dadosIR, requisitado, loading } = useSelector<
    StoreState,
    ImpostoRendaState
  >((state) => state.impostoRendaState);
  const [totalRendimentos, settotalRendimentos] = useState(0);
  const [
    contribuicaoPrevidenciariaOficial,
    setcontribuicaoPrevidenciariaOficial,
  ] = useState(0);
  const [impostoRetidonaFonte, setimpostoRetidonaFonte] = useState(0);
  const [pensaoAlimeticia, setpensaoAlimeticia] = useState(0);
  const [ajudaCustos, setajudaCustos] = useState(0);

  const setFiltros = (value: {}) => {
    dispatch(
      impostoRendaActions.setFiltros({
        ...filtros,
        ...value,
      })
    );
  };

  const pesquisarDemostrativo = useCallback(
    async (ano: string) => {
      await dispatch(impostoRendaActions.buscarDemonstrativoIR(ano));
    },
    [dispatch]
  );

  const gerarPDF = useCallback(async () => {
    const tabelaIR = document.getElementById("conteudoParaPdf");

    if (tabelaIR === null) {
      return message.error("Erro ao gerar PDF");
    }
    const { userData } = await getSession();

    downloadPDFPaginaUnica(`relatorio_IR_${userData?.CPF}`, tabelaIR);
  }, []);

  useEffect(() => {
    if (!requisitado) {
      dispatch(impostoRendaActions.inicializaEstado());
    }
  }, [dispatch, requisitado]);

  useEffect(() => {
    if (dadosIR) {
      dadosIR.forEach((verba) => {
        switch (verba.VerbaID) {
          case 1:
            setcontribuicaoPrevidenciariaOficial(verba.Valor);
            break;
          case 2:
            setimpostoRetidonaFonte(verba.Valor);
            break;
          case 5:
            setpensaoAlimeticia(verba.Valor);
            break;
          case 43:
            settotalRendimentos(verba.Valor);
            break;
          case 107:
            setajudaCustos(verba.Valor);
            break;
        }
      });
    }
  }, [dadosIR]);

  return (
    <ContainerPagina>
      <PageTitle titulo="Comprovante de Rendimentos Pagos e de IR" />
      <hgroup></hgroup>

      <FormFiltros>
        <div>
          <InputLabelSelect
            labelText="Ano"
            labelId="ano"
            options={listaAnos.map((item) => {
              return { label: item.toString(), value: item.toString() };
            })}
            value={filtros.ano}
            onChange={(value) => {
              setFiltros({ ano: value });
              pesquisarDemostrativo(value);
            }}
            required
          />
        </div>
        <div>
          <Button
            onClick={(event) => {
              event.preventDefault();
              gerarPDF();
            }}
            primary
          >
            <DownloadOutlined style={{ marginRight: "10px" }} />
            Gerar PDF
          </Button>
        </div>
      </FormFiltros>

      <div className="tabela">
        {!loading && dadosIR && dadosIR.length > 0 ? (
          <table id="conteudoParaPdf">
            <tbody>
              <tr>
                <td>
                  <header>
                    <AreaInfo>
                      <tbody>
                        <tr>
                          <td className="ministerioEconomia">
                            <img
                              src={ministerioEconomia}
                              alt="Ministério da Economia"
                            />
                            <span>
                              MINISTÉRIO DA ECONOMIA
                              <br />
                              Secretaria Especial da Receita Federal do Brasil
                              <br />
                              Imposto sobre a Renda da Pessoa Física
                              <br />
                              <strong>
                                Exercício de {new Date().getFullYear()}
                              </strong>
                            </span>
                          </td>
                          <td className="containerInfos">
                            Comprovante de Rendimentos Pagos e de Imposto sobre
                            a Renda Retido na Fonte
                            <strong>Ano-Calendario: {dadosIR[0].Ano}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </AreaInfo>

                    <AreaObservacao>
                      Verifique as condições e o prazo para a apresentação da
                      Declaração do Imposto sobre a Renda da Pessoa Física para
                      este ano calendário no sítio da Secretaria Especial da
                      Receita Federal do Brasil na Internet, no endereço.
                    </AreaObservacao>
                  </header>

                  <main>
                    <AreaFontePagadora>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>1. Fonte Pagadora Pessoa Jurídica</td>
                        </tr>
                        <tr>
                          <td>
                            CNPJ/CPF
                            <br />
                            {dadosIR[0].CNPJDeclaranteFormatado}
                          </td>
                          <td>
                            Nome Empresarial / Nome
                            <br />
                            {dadosIR[0].NomeDeclarante}
                          </td>
                        </tr>
                      </tbody>
                    </AreaFontePagadora>

                    <AreaBeneficiario>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>2. Pessoa Física Beneficiária dos Rendimentos</td>
                        </tr>
                        <tr id="dadosBeneficiario">
                          <td>
                            CPF
                            <br />
                            <strong>{formatCPF(dadosIR[0].CPF)}</strong>
                          </td>
                          <td>
                            Nome Completo
                            <br />
                            <strong>{dadosIR[0].Nome}</strong>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            Natureza do Rendimento
                            <br />
                            Rendimentos sem Vínculo Empregatício
                          </td>
                        </tr>
                      </tbody>
                    </AreaBeneficiario>

                    <LinhasTabela>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>
                            3. Rendimentos tributáveis, Deduções e imposto
                            retido na fonte
                          </td>
                          <td>Valores em (R$)</td>
                        </tr>
                        <tr>
                          <td>01. Total dos Rendimentos (inclusive Férias)</td>
                          <td>{formatMoney(totalRendimentos)}</td>
                        </tr>

                        <tr>
                          <td>02. Contribuição Previdênciária Oficial</td>
                          <td>
                            {formatMoney(contribuicaoPrevidenciariaOficial)}
                          </td>
                        </tr>

                        <tr>
                          <td>
                            03. Contribuição à previdência privada e ao FAPI
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            04. Pensão Alimetícia (informar na ficha inf.
                            complementar)
                          </td>
                          <td>{formatMoney(pensaoAlimeticia)}</td>
                        </tr>

                        <tr>
                          <td>05. Imposto Retido na Fonte</td>
                          <td>{formatMoney(impostoRetidonaFonte)}</td>
                        </tr>
                      </tbody>
                    </LinhasTabela>

                    <LinhasTabela>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>4. Rendimentos Isentos e Não Tributáveis</td>
                          <td>Valores em (R$)</td>
                        </tr>
                        <tr>
                          <td>
                            01. Parcela isenta dos Prov. de Apos. Reserva,
                            Refor. e Pensão (65 anos ou mais)
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>02. Diárias e Ajuda de Custo</td>
                          <td>{formatMoney(ajudaCustos)}</td>
                        </tr>

                        <tr>
                          <td>
                            03. Pensão, Prov. de Aposent. ou Reformas por
                            Molestia Grave e Aposentadoria ou reforma por
                            Acidente em Serviço.
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            04. Lucro e Dividendo apurado a partir de 1996 pago
                            por PJ (Lucro Real, Presumido ou Arbitrado)
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            05. Valores Pagos ao Titular ou Sócio da
                            Microempresa ou Empresa de Pequeno porte exceto
                            Pró-labore, Alugueis ou Serviços Prestados
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            06. Indenizações por resc. contr. trab., inclusive
                            PDV, e acid de trabalho
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>07. Outros (especificar)</td>
                          <td>{formatMoney(0)}</td>
                        </tr>
                      </tbody>
                    </LinhasTabela>

                    <LinhasTabela>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>
                            5. Rendimentos Sujeitos à Tributação Exclusiva
                            (rendimento líquido)
                          </td>
                          <td>Valores em (R$)</td>
                        </tr>
                        <tr>
                          <td>01. Décimo Terceiro Salário</td>
                          <td>{formatMoney(0)}</td>
                        </tr>
                        <tr>
                          <td>02. Outros</td>
                          <td>{formatMoney(0)}</td>
                        </tr>
                      </tbody>
                    </LinhasTabela>

                    <LinhasTabela>
                      <tbody>
                        <tr className="tituloSecao comSubtitulo">
                          <td>
                            6. Rendimentos Recebidos Acumuladamente - Art. 12-A
                            da Lei nº 7.713, de 1988 (sujeitos à tributação
                            exclusiva)
                            <table>
                              <tbody>
                                <tr className="subtituloSecao">
                                  <td>
                                    <span>
                                      6.1 Número do processo: (especificar)
                                    </span>
                                    <span>Quantidade de meses </span>
                                    <span>0,00</span>
                                  </td>
                                  <td>Natureza do rendimento: (especificar)</td>
                                </tr>
                                <tr>
                                  <td>Valores em (R$)</td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            1. Total dos rendimentos tributáveis (inclusive
                            férias e décimo terceiro salário)
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>02. Exclusão: Despesas com a ação judicial</td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            03. Dedução: Contribuição previdenciária oficial
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            04. Dedução: Pensão alimentícia (preencher também o
                            quadro 7)
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>05. Imposto sobre a renda retido na fonte</td>
                          <td>{formatMoney(0)}</td>
                        </tr>

                        <tr>
                          <td>
                            06. Rendimentos isentos de pensão, proventos de
                            aposentadoria ou reforma por moléstia grave ou
                            aposentadoria ou reforma por acidente em serviço
                          </td>
                          <td>{formatMoney(0)}</td>
                        </tr>
                      </tbody>
                    </LinhasTabela>

                    <AreaInfoComplementares>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>7. Informações Complementares</td>
                        </tr>
                        <tr id="espacoEmBranco">
                          <td></td>
                        </tr>
                      </tbody>
                    </AreaInfoComplementares>

                    <AreaResponsavelInfo>
                      <tbody>
                        <tr className="tituloSecao">
                          <td>8. Responsável pelas Informações</td>
                        </tr>
                        <tr>
                          <td>
                            <span>Nome</span>
                            <span>{dadosIR[0].NomeResponsavel}</span>
                          </td>
                          <td>
                            <span>Data</span>
                            <span>{formatData(new Date().toISOString())}</span>
                          </td>
                          <td id="assinatura">Assinatura</td>
                        </tr>
                      </tbody>
                    </AreaResponsavelInfo>
                  </main>
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <IndicadorSemDados />
        )}
      </div>
    </ContainerPagina>
  );
}

export default ImpostoRenda;
